import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useCallback, useEffect, useState } from 'react';
import {
  $createParagraphNode,
  $getSelection,
  $isRangeSelection,
} from 'lexical';
import {
  $createHeadingNode,
  $createQuoteNode,
  $isHeadingNode,
  HeadingTagType,
} from '@lexical/rich-text';
import { $setBlocksType } from '@lexical/selection';
import { $getNearestNodeOfType, mergeRegister } from '@lexical/utils';
import { $isListNode, ListNode } from '@lexical/list';
import { richTextEditorStrings } from '../../../i18n/components/richtTextEditor';

type BlockType = 'paragraph' | 'h1' | 'h2' | 'quote' | 'ul' | 'ol';

export function BlockFormatPlugin() {
  const [editor] = useLexicalComposerContext();
  const [blockType, setBlockType] = useState<BlockType>('paragraph');

  const updateBlockType = useCallback(() => {
    const selection = $getSelection();

    if (!$isRangeSelection(selection)) {
      return;
    }

    const anchorNode = selection.anchor.getNode();
    const isRootNode = anchorNode.getKey() === 'root';
    const element = isRootNode
      ? anchorNode
      : anchorNode.getTopLevelElementOrThrow();

    const elementKey = element.getKey();
    const elementDOM = editor.getElementByKey(elementKey);

    if (!elementDOM) {
      return;
    }

    if ($isListNode(element)) {
      const parentList = $getNearestNodeOfType(anchorNode, ListNode);
      const listType = parentList
        ? parentList.getListType()
        : element.getListType();
      const blockTypeValue = listType === 'bullet' ? 'ul' : 'ol';
      setBlockType(blockTypeValue);
      return;
    }

    const elementType = $isHeadingNode(element)
      ? element.getTag()
      : element.getType();

    if (elementType === 'h1' || elementType === 'h2') {
      setBlockType(elementType);
      return;
    }

    if (elementType === 'quote') {
      setBlockType('quote');
      return;
    }

    setBlockType('paragraph');
  }, [editor]);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          updateBlockType();
        });
      }),
    );
  }, [editor, updateBlockType]);

  const formatParagraph = () => {
    editor.update(() => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection)) {
        return;
      }
      $setBlocksType(selection, () => $createParagraphNode());
    });
  };

  const formatHeading = (headingSize: HeadingTagType) => {
    if (blockType === headingSize) {
      formatParagraph();
      return;
    }

    editor.update(() => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection)) {
        return;
      }
      $setBlocksType(selection, () => $createHeadingNode(headingSize));
    });
  };

  const formatQuote = () => {
    if (blockType === 'quote') {
      formatParagraph();
      return;
    }

    editor.update(() => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection)) {
        return;
      }
      $setBlocksType(selection, () => $createQuoteNode());
    });
  };

  return (
    <span className="lexical-toolbar-group">
      <button
        type="button"
        onClick={() => formatHeading('h1')}
        className={`lexical-toolbar-button ${blockType === 'h1' ? 'active' : ''}`}
        aria-label={richTextEditorStrings.blockFormat.heading1.label}
        title={richTextEditorStrings.blockFormat.heading1.title}
      >
        H1
      </button>
      <button
        type="button"
        onClick={() => formatHeading('h2')}
        className={`lexical-toolbar-button ${blockType === 'h2' ? 'active' : ''}`}
        aria-label={richTextEditorStrings.blockFormat.heading2.label}
        title={richTextEditorStrings.blockFormat.heading2.title}
      >
        H2
      </button>
      <button
        type="button"
        onClick={formatQuote}
        className={`lexical-toolbar-button ${blockType === 'quote' ? 'active' : ''}`}
        aria-label={richTextEditorStrings.blockFormat.quote.label}
        title={richTextEditorStrings.blockFormat.quote.title}
      >
        <i className="pi pi-comments" />
      </button>
    </span>
  );
}
